import axios from 'axios';

import {apiClient} from 'core/apiClient';
import {AuthData, AuthRequest, LegalPerson, ProfileRaw, TokensData, TokensDataRaw} from 'types';

import {CertData, CryptoProCert} from '../types/plugins/cadesPlugin';

import {parseAuthData, parseProfile, parseTokensData} from './mappers/auth';

const AUTH_URL = '/uaa/default';

export async function authorize({
    login,
    password,
    saveCredentials,
}: AuthRequest): Promise<[AuthData, boolean]> {
    const path = `/uaa/web/login`;
    const response = (
        await apiClient.post<{token: TokensDataRaw; profile: ProfileRaw}>(path, {
            email: login,
            password,
        })
    ).data;
    const parsedAuthData = parseAuthData({...response.token, profile: response.profile});
    return [parsedAuthData, saveCredentials];
}

export async function deauthorize(refreshToken: string | null): Promise<void> {
    if (!refreshToken) return;

    const path = `${AUTH_URL}/logout`;
    const params = new URLSearchParams();
    params.append('refreshToken', refreshToken);
    const config = {
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
        },
    };
    await apiClient.post(path, params, config).catch(console.error);
}

export async function fetchAccessToken(refreshToken: string): Promise<TokensData> {
    const path = `${AUTH_URL}/refresh-token`;
    const params = new URLSearchParams();
    params.append('refreshToken', refreshToken);
    const config = {
        headers: {'Content-Type': 'application/x-www-form-urlencoded'},
        baseURL: '/api/v1',
    };

    const response = (await axios.post(path, params, config)).data as TokensDataRaw;
    return parseTokensData(response);
}

export async function fetchProfile() {
    const path = `uaa/web/profile`;
    return apiClient.get(path).then((response) => parseProfile(response.data as ProfileRaw));
}

export async function getExtendedCert(cert: CryptoProCert | null) {
    if (!cert) throw new Error('Certificate not found');
    return apiClient.post<CertData>('uaa/web/eds/validate-and-parse', cert);
}

export async function getLegalPersons(cert: CertData) {
    return apiClient.post<{organizations: LegalPerson[]}>('uaa/web/eds/login', cert);
}

export async function authorizeWithCert({cert, id}: {cert: CertData; id: number}) {
    const payload = {
        login: cert,
        legal_identity_id: id,
    };

    const authData = (
        await apiClient.post<{token: TokensDataRaw; profile: ProfileRaw}>(
            'uaa/web/eds/post-login',
            payload,
        )
    ).data;

    const parsedAuthData = parseAuthData({...authData.token, profile: authData.profile});
    return [parsedAuthData];
}
